"use client";

import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, MapPin, Users, Calendar, Star } from "lucide-react";
import Image from "next/image";
import { Button } from "@/components/ui/button";

export interface Hunt {
  id: number;
  title: string;
  description: string;
  image?: string;
  status: "active" | "completed" | "upcoming" | "ended";
  participants: number;
  locations: number;
  rating: number;
  date: string;
  category: string;
  difficulty: "Easy" | "Medium" | "Hard";
}

export interface HuntCarouselProps {
  hunts?: Hunt[];
  isLoading?: boolean;
  error?: string;
  autoPlayInterval?: number;
  enableAutoPlay?: boolean;
  onHuntClick?: (hunt: Hunt) => void;
  onBrowseAllClick?: () => void;
}

// Mock data for demonstration - in real app, this would come from API
const mockHunts: Hunt[] = [
  {
    id: 1,
    title: "Downtown Art Discovery",
    description: "Explore the vibrant street art scene and discover hidden murals throughout the city center.",
    image: "/api/placeholder/400/250",
    status: "active",
    participants: 234,
    locations: 8,
    rating: 4.8,
    date: "2024-01-15",
    category: "Art & Culture",
    difficulty: "Easy"
  },
  {
    id: 2,
    title: "Historic Landmarks Quest",
    description: "Journey through time as you visit iconic historical sites and learn fascinating stories.",
    image: "/api/placeholder/400/250",
    status: "active",
    participants: 156,
    locations: 12,
    rating: 4.9,
    date: "2024-01-10",
    category: "History",
    difficulty: "Medium"
  },
  {
    id: 3,
    title: "Tech Startup Challenge",
    description: "Corporate team building adventure through Silicon Valley's most innovative spaces.",
    image: "/api/placeholder/400/250",
    status: "completed",
    participants: 89,
    locations: 6,
    rating: 4.7,
    date: "2023-12-20",
    category: "Corporate",
    difficulty: "Hard"
  },
  {
    id: 4,
    title: "University Campus Mystery",
    description: "Student orientation scavenger hunt covering all the essential campus locations.",
    image: "/api/placeholder/400/250",
    status: "active",
    participants: 312,
    locations: 15,
    rating: 4.6,
    date: "2024-01-08",
    category: "Education",
    difficulty: "Easy"
  },
  {
    id: 5,
    title: "Food Truck Festival Hunt",
    description: "Taste your way through the city's best food trucks while completing fun challenges.",
    image: "/api/placeholder/400/250",
    status: "completed",
    participants: 445,
    locations: 10,
    rating: 4.9,
    date: "2023-11-15",
    category: "Food & Drink",
    difficulty: "Easy"
  }
];

export function HuntCarousel({
  hunts = mockHunts,
  isLoading = false,
  error,
  autoPlayInterval = 5000,
  enableAutoPlay = true,
  onHuntClick,
  onBrowseAllClick,
}: HuntCarouselProps = {}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(enableAutoPlay);

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying || hunts.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % hunts.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, hunts.length, autoPlayInterval]);

  const nextSlide = () => {
    if (hunts.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % hunts.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    if (hunts.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + hunts.length) % hunts.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const handleHuntClick = (hunt: Hunt) => {
    if (onHuntClick) {
      onHuntClick(hunt);
    }
  };

  const handleBrowseAllClick = () => {
    if (onBrowseAllClick) {
      onBrowseAllClick();
    }
  };

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Featured Scavenger Hunts
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover amazing adventures created by our community. From corporate team building
            to educational tours, there&apos;s something for everyone.
          </p>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <p className="text-red-800 text-lg">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && hunts.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Hunts Available</h3>
            <p className="text-gray-600 mb-6">
              There are no scavenger hunts to display at the moment. Check back soon for new adventures!
            </p>
            <Button onClick={handleBrowseAllClick} variant="outline" size="lg">
              Explore Other Options
            </Button>
          </div>
        )}

        {/* Carousel */}
        {!isLoading && !error && hunts.length > 0 && (
        <div className="relative">
          <div className="overflow-hidden rounded-2xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {hunts.map((hunt) => (
                <div key={hunt.id} className="w-full flex-shrink-0">
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden mx-2">
                    <div className="md:flex">
                      {/* Image */}
                      <div className="md:w-1/2">
                        <div className={`h-64 md:h-full ${hunt.image ? '' : 'bg-gradient-to-br from-blue-400 to-purple-500'} relative`}>
                          {hunt.image && (
                            <Image
                              src={hunt.image}
                              alt={hunt.title}
                              fill
                              className="object-cover"
                              sizes="(max-width: 768px) 100vw, 50vw"
                            />
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                          <div className="absolute top-4 left-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              hunt.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : hunt.status === 'upcoming'
                                ? 'bg-blue-100 text-blue-800'
                                : hunt.status === 'ended'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {hunt.status.charAt(0).toUpperCase() + hunt.status.slice(1)}
                            </span>
                          </div>
                          <div className="absolute bottom-4 left-4 text-white">
                            <div className="flex items-center space-x-4 text-sm">
                              <div className="flex items-center">
                                <Users className="w-4 h-4 mr-1" />
                                {hunt.participants}
                              </div>
                              <div className="flex items-center">
                                <MapPin className="w-4 h-4 mr-1" />
                                {hunt.locations}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="md:w-1/2 p-8">
                        <div className="flex items-center justify-between mb-4">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                            {hunt.category}
                          </span>
                          <div className="flex items-center">
                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                            <span className="ml-1 text-sm font-medium text-gray-700">{hunt.rating}</span>
                          </div>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          {hunt.title}
                        </h3>

                        <p className="text-gray-600 mb-6 leading-relaxed">
                          {hunt.description}
                        </p>

                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(hunt.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            hunt.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                            hunt.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {hunt.difficulty}
                          </span>
                        </div>

                        <Button
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={() => handleHuntClick(hunt)}
                        >
                          {hunt.status === 'active' ? 'Join Hunt' : 'View Details'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>

          {/* Dots indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {hunts.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
        )}

        {/* Bottom CTA */}
        {!isLoading && !error && (
        <div className="text-center mt-12">
          <Button variant="outline" size="lg" className="px-8" onClick={handleBrowseAllClick}>
            Browse All Hunts
          </Button>
        </div>
        )}
      </div>
    </section>
  );
}
